// L'écran d'accueil mène le parcours dans l'ordre du cahier des charges (05) :
// lien personnel → installation (iPhone) → consentement → rappels activés et
// test reçu → réglages (elle, une fois) → écran du jour.
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useReducer } from 'react';
import { useSearchParams } from 'react-router';

import { Chargement, ErreurReseau } from '../components/base';
import { ErreurApi, type Moi } from '../lib/api';
import { jetonCourant, noterTestRecu, oublierTout, testRecu } from '../lib/jeton';
import { estInstallee, estIos, etatPush, rafraichirAbonnement } from '../lib/push';
import { useMoi } from '../lib/requetes';
import { ActiverRappels } from './ActiverRappels';
import { AujourdhuiElle } from './Aujourdhui';
import { AujourdhuiProche } from './AujourdhuiProche';
import { ChoixConfirmation } from './ChoixConfirmation';
import { Consentement } from './Consentement';
import { GuideInstallation } from './GuideInstallation';
import { etapeDuParcours } from './parcours';
import { EcranReglages } from './Reglages';
import { LienRefuse, SansLien } from './SansLien';

/** Un toucher sur la notification de test vaut « Je l'ai reçue ». */
function useTestRecuParLien() {
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();
  useEffect(() => {
    if (params.get('test') !== 'recu') return;
    noterTestRecu();
    setParams({}, { replace: true });
    void client.invalidateQueries();
  }, [params, setParams, client]);
}

function Parcours({ moi, relancer }: Readonly<{ moi: Moi; relancer: () => void }>) {
  const etape = etapeDuParcours(moi, {
    iosNonInstallee: estIos() && !estInstallee(),
    pushActif: etatPush() === 'actif',
    testRecu: testRecu(),
  });
  useEffect(() => {
    // Un abonnement renouvelé par le navigateur ne doit pas se perdre.
    if (etape === 'regler' || etape === 'confirmation' || etape === 'jour')
      void rafraichirAbonnement(moi.clePublique).catch(() => null);
  }, [etape, moi.clePublique]);

  switch (etape) {
    case 'installer':
      return <GuideInstallation />;
    case 'consentir':
      return <Consentement role={moi.role} version={moi.consentement.versionCourante} />;
    case 'activer':
      return (
        <ActiverRappels
          role={moi.role}
          clePublique={moi.clePublique}
          perdu={moi.abonnementPerduLe !== null}
          onTermine={relancer}
        />
      );
    case 'regler':
      return <EcranReglages premiereFois />;
    case 'confirmation':
      return <ChoixConfirmation actuel={null} />;
    default:
      return moi.role === 'elle' ? (
        <AujourdhuiElle
          fuseau={moi.reglages.fuseau}
          procheAbonnementPerduLe={moi.procheAbonnementPerduLe}
        />
      ) : (
        <AujourdhuiProche
          fuseau={moi.reglages.fuseau}
          prevenir={moi.reglages.prevenirProche}
          aChaquePrise={moi.confirmation === 'chaque-prise'}
        />
      );
  }
}

export function Accueil() {
  const [, relancer] = useReducer((n: number) => n + 1, 0);
  const moi = useMoi();
  useTestRecuParLien();
  if (!jetonCourant()) return <SansLien onLien={relancer} />;
  if (moi.error instanceof ErreurApi && moi.error.status === 401) {
    return (
      <LienRefuse
        onAutre={() => {
          oublierTout();
          relancer();
        }}
      />
    );
  }
  if (moi.isError) return <ErreurReseau onReessayer={() => void moi.refetch()} />;
  if (!moi.data) return <Chargement />;
  return <Parcours moi={moi.data} relancer={relancer} />;
}
